import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { taskApi } from '../lib/api';
import { CalendarIcon, Clock, Plus, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const TaskModal = ({ open, onClose, onSave, task, defaultDate }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [dueTime, setDueTime] = useState('');
  const [priority, setPriority] = useState('medium');
  const [labels, setLabels] = useState([]);
  const [subtasks, setSubtasks] = useState([]);
  const [isRecurring, setIsRecurring] = useState(false);
  const [frequency, setFrequency] = useState('daily');
  const [interval, setInterval] = useState(1);
  const [weekdays, setWeekdays] = useState([]);
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [newSubtask, setNewSubtask] = useState('');

  const labelOptions = ['Work', 'Personal', 'Study', 'Health'];
  const weekdayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  useEffect(() => {
    if (task) {
      setTitle(task.title || '');
      setDescription(task.description || '');
      setDueDate(task.due_date || '');
      setDueTime(task.due_time || '');
      setPriority(task.priority || 'medium');
      setLabels(task.labels || []);
      setSubtasks(task.subtasks || []);
      setIsRecurring(task.is_recurring || false);
      if (task.recurrence_rule) {
        setFrequency(task.recurrence_rule.frequency || 'daily');
        setInterval(task.recurrence_rule.interval || 1);
        setWeekdays(task.recurrence_rule.weekdays || []);
        setEndDate(task.recurrence_rule.end_date || '');
      }
    } else {
      setTitle('');
      setDescription('');
      setDueDate(defaultDate || '');
      setDueTime('');
      setPriority('medium');
      setLabels([]);
      setSubtasks([]);
      setIsRecurring(false);
      setFrequency('daily');
      setInterval(1);
      setWeekdays([]);
      setEndDate('');
    }
  }, [task, defaultDate, open]);

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast.error('Task title is required');
      return;
    }

    setLoading(true);
    try {
      const data = {
        title,
        description,
        due_date: dueDate,
        due_time: dueTime,
        priority,
        labels,
        subtasks,
        is_recurring: isRecurring,
        recurrence_rule: isRecurring ? { frequency, interval, weekdays, ...(endDate ? { end_date: endDate } : {}) } : null
      };

      if (task) {
        await taskApi.update(task.task_id, data);
        toast.success('Task updated!');
      } else {
        await taskApi.create(data);
        toast.success('Task created!');
      }
      if (onSave) onSave();
    } catch (error) {
      toast.error('Failed to save task');
    } finally {
      setLoading(false);
    }
  };

  const toggleLabel = (label) => {
    if (labels.includes(label)) {
      setLabels(labels.filter(l => l !== label));
    } else {
      setLabels([...labels, label]);
    }
  };

  const addSubtask = () => {
    if (!newSubtask.trim()) return;
    setSubtasks([...subtasks, { title: newSubtask, completed: false }]);
    setNewSubtask('');
  };

  const removeSubtask = (index) => {
    setSubtasks(subtasks.filter((_, i) => i !== index));
  };

  const toggleWeekday = (day) => {
    if (weekdays.includes(day)) {
      setWeekdays(weekdays.filter(d => d !== day));
    } else {
      setWeekdays([...weekdays, day]);
    }
  };

  const renderLabels = () => {
    return labelOptions.map((label) => {
      const isSelected = labels.includes(label);
      return (
        <Badge
          key={label}
          variant={isSelected ? 'default' : 'outline'}
          className={isSelected ? 'cursor-pointer bg-[#4A9CC7]' : 'cursor-pointer'}
          onClick={() => toggleLabel(label)}
          data-testid={`label-${label.toLowerCase()}`}
        >
          {label}
        </Badge>
      );
    });
  };

  const renderSubtasks = () => {
    return subtasks.map((subtask, i) => (
      <div key={i} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
        <span className="flex-1 text-sm">{subtask.title}</span>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => removeSubtask(i)}
          className="h-6 w-6"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>
    ));
  };

  const renderWeekdays = () => {
    return weekdayLabels.map((label, idx) => {
      const isSelected = weekdays.includes(idx);
      return (
        <Badge
          key={idx}
          variant={isSelected ? 'default' : 'outline'}
          className={isSelected ? 'cursor-pointer bg-[#4A9CC7]' : 'cursor-pointer'}
          onClick={() => toggleWeekday(idx)}
        >
          {label}
        </Badge>
      );
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="task-modal">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Manrope' }}>
            {task ? 'Edit Task' : 'New Task'}
          </DialogTitle>
          <DialogDescription>
            {task ? 'Update your task details below' : 'Add a new task to your list'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="What needs to be done?"
              className="rounded-xl"
              data-testid="task-title-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add details..."
              className="rounded-xl resize-none"
              rows={3}
              data-testid="task-description-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="due-date">Due Date</Label>
              <div className="relative">
                <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="due-date"
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="pl-10 rounded-xl"
                  data-testid="task-date-picker"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="time">Due Time</Label>
              <div className="relative">
                <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="time"
                  type="time"
                  value={dueTime}
                  onChange={(e) => setDueTime(e.target.value)}
                  className="pl-10 rounded-xl"
                  data-testid="task-time-input"
                />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Priority</Label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger className="rounded-xl" data-testid="task-priority-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Labels</Label>
            <div className="flex flex-wrap gap-2">
              {renderLabels()}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Subtasks</Label>
            <div className="flex gap-2">
              <Input
                value={newSubtask}
                onChange={(e) => setNewSubtask(e.target.value)}
                placeholder="Add a subtask"
                className="rounded-xl"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addSubtask();
                  }
                }}
                data-testid="subtask-input"
              />
              <Button 
                type="button" 
                variant="outline" 
                onClick={addSubtask}
                className="rounded-xl"
                data-testid="add-subtask-btn"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {subtasks.length > 0 && (
              <div className="space-y-2 mt-2">
                {renderSubtasks()}
              </div>
            )}
          </div>

          <div className="space-y-4 p-4 bg-gray-50 rounded-xl">
            <div className="flex items-center justify-between">
              <Label>Recurring Task</Label>
              <Switch
                checked={isRecurring}
                onCheckedChange={setIsRecurring}
                data-testid="recurring-switch"
              />
            </div>
            
            {isRecurring && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Frequency</Label>
                    <Select value={frequency} onValueChange={setFrequency}>
                      <SelectTrigger className="rounded-xl" data-testid="recurrence-frequency">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="custom">Custom</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Every</Label>
                    <Input
                      type="number"
                      min="1"
                      value={interval}
                      onChange={(e) => setInterval(parseInt(e.target.value) || 1)}
                      className="rounded-xl"
                      data-testid="recurrence-interval"
                    />
                  </div>
                </div>
                
                {frequency === 'weekly' && (
                  <div className="space-y-2">
                    <Label>Repeat on</Label>
                    <div className="flex flex-wrap gap-2">
                      {renderWeekdays()}
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>End Date (optional)</Label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="rounded-xl"
                    data-testid="recurrence-end-date"
                    placeholder="No end date"
                  />
                  {endDate && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-xs h-7 px-2"
                      onClick={() => setEndDate('')}
                      data-testid="clear-end-date-btn"
                    >
                      Clear end date
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full" data-testid="cancel-btn">
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={loading}
            className="bg-[#4A9CC7] hover:bg-[#3A8CB7] text-white rounded-full"
            data-testid="save-task-btn"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            {task ? 'Update Task' : 'Create Task'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TaskModal;
